// export default App;

import React, { useEffect, useState } from "react";

import dayjs from "dayjs";
import "dayjs/locale/en-gb";
// Set global locale for dayjs to use DD/MM/YYYY formatting

import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  useLocation,
  Outlet,
  useLoaderData,
} from "react-router-dom";

import { SnackbarAlertProvider } from "./hooks/SnackbarAlert"; // named import
import { AppProvider } from "./context/AppContext";
import { AuthProvider } from "./auth/AuthContext";

import Sidebar from "./components/Sidebar";
import Header from "./components/Header";

import EmployeeForm from "./pages/Employees/EmployeeForm";
import CustomerForm from "./pages/Customers/CustomerForm";
import VehicleForm from "./pages/Vehicles/VehicleForm";
import VendorForm from "./pages/Vendors/VendorForm";

import ClientRegisterPage from "./auth/ClientRegisterPage";
import RegisterPage from "./auth/RegisterPage";
import LoginPage from "./auth/LoginPage";
import ChangePasswordPage from "./auth/ChangePasswordPage";

import SubscriptionExpired from "./pages/SubscriptionExpired";
import ActivationPayment from "./pages/ActivationPayment";
import SubscriptionPayment from "./pages/SubscriptionPayment";

import DriverAppPaymentSubmit from "./pages/DriverAppPaymentSubmit";

import ProtectedRoute from "./auth/ProtectedRoute";
import PublicRoute from "./auth/PublicRoute";

import SimpleLayout from "./SimpleLayout";
import TermsPage from "./TermsPage";
import PrivacyPage from "./PrivacyPage";

import HomePage from "./pages/HomePage";
import UserProfilePage from "./pages/UserProfilePage";
import SettingsRoute from "./components/settings/SettingsRoute";
import SimpleClientData from "./components/SimpleClientData";
import { ToastContainer } from "react-toastify";
import CategoryForm from "./components/CategoryForm"; // adjust import path
import { useAppContext } from "./context/AppContext"; // your context hook
import HotelPage from "./pages/HotelPage";
import BunkForm from "./pages/FuelIndent/BunkForm.jsx"; // Adjust path as per
import BookingForm from "./pages/Bookings/BookingForm.jsx";

import TripsheetForm from "./pages/Tripsheets/TripsheetForm.jsx"; // adjust import path correctly
import TripsheetFormAdd from "./pages/Tripsheets/TripsheetFormAdd.jsx";

import InvoiceForm from "./pages/Invoices/InvoiceForm.jsx"; // adjust path per your project
import InvoiceFormLocal from "./pages/Invoices/InvoiceFormLocal.jsx"; // adjust the path as needed
import PurchaseForm from "./pages/Purchases/PurchaseForm.jsx";
import PurchaseFormAdd from "./pages/Purchases/PurchaseFormAdd.jsx";

import AccountsChart from "./pages/Accountant/AccountsChart.jsx";
import VoucherList from "./pages/Accountant/VoucherList.jsx"; // update the path as per your folder structure\
import AccountsReportsDashboard from "./pages/Accountant/AccountsReportsDashboard";
import LedgerStatement from "./pages/Accountant/LedgerStatement";
import OutstandingStatement from "./pages/Accountant/OutstandingStatement";

import IndentForm from "./pages/FuelIndent/IndentForm.jsx";
import IndentFormAdd from "./pages/FuelIndent/IndentFormAdd.jsx"; // adjust the path as needed

import AdminNumberingConfigPage from "./pages/AdminNumberingConfigPage";
import ReportsDashboard from "./reports/ReportsDashboard";
import CustomersReport from "./reports/CustomersReport";
import VehicleReport from "./reports/VehicleReport";
import VendorReport from "./reports/VendorReport";
import BookingReport from "./reports/BookingReport";
import TariffReport from "./reports/TariffReport";
import FuelIndentReport from "./reports/FuelIndentReport";
import TripStatementReport from "./reports/TripStatementReport";
import ComparisonReport from "./reports/ComparisonReport";
import SaleRegister from "./reports/SaleRegister";
import PurchaseReport from "./reports/PurchaseReport";
import GSTReport from "./reports/GSTReport";
import MISCustomerVehicle from "./reports/MISCustomerVehicle";

import ForgotPasswordPage from "./auth/ForgotPasswordPage";
import ResetPasswordPage from "./auth/ResetPasswordPage";

import EncryptionTest from "./utils/EncryptionTest";


import "./App.css";

dayjs.locale("en-gb");

// ---------------- Page Title Helper ----------------
const getPageTitle = (pathname) => {
  switch (pathname) {
    case "/":
      return "Dashboard";
    case "/employees":
      return "Employees";
    case "/customers":
      return "Customers";
    case "/vehicles":
      return "Vehicles";
    case "/vendors":
      return "Vendors";
    case "/bookings":
      return "Bookings";
    case "/tripsheets":
      return "Tripsheets";
    case "/invoices":
      return "Local Invoices";
    case "/purchases":
      return "Purchase";
    case "/vouchers":
      return "Vouchers";
    case "/indents":
      return "Fuel Indents";

    default:
      return "WheelsERP";
  }
};

// ---------------- Layout ----------------
const MainLayout = () => {
  const location = useLocation();
  const title = getPageTitle(location.pathname);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const sidebarWidth = sidebarCollapsed ? 64 : 200;

  return (
    <div className="h-screen flex">
      {/* Sidebar */}
      <div
        className="fixed top-0 left-0 h-full z-40 bg-white border-r"
        style={{
          width: `${sidebarWidth}px`,
          minWidth: `${sidebarWidth}px`,
          transition: "width 200ms ease-in-out",
        }}
      >
        <Sidebar onCollapsedChange={setSidebarCollapsed} />
      </div>

      {/* Content */}
      <div
        className="flex flex-col flex-1 min-w-0"
        style={{
          marginLeft: `${sidebarWidth}px`,
          transition: "margin-left 200ms ease-in-out",
        }}
      >
        {/* Header */}
        <div
          className="h-14 fixed top-0 right-0 z-30 bg-white border-b"
          style={{
            left: `${sidebarWidth}px`,
            transition: "left 200ms ease-in-out",
          }}
        >
          <Header title={title} sidebarWidth={sidebarWidth} />
        </div>

        {/* Main Area */}
        <main
          className="bg-gray-50 overflow-y-auto overflow-x-hidden scroll-thin min-w-0 w-full"
          style={{
            height: `calc(100vh - 56px)`,
            marginTop: "56px",
          }}
        >
          <div className="p-2 min-w-0 w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

// ---------------- Loader Functions ----------------
// NOTE: All loaders below now include client_id, since the backend
// requires it on every list endpoint. Missing client_id was causing
// silent "Client ID required" failures on every navigation to these
// routes (confirmed via /api/bookings — same root cause fixed here
// for every other loader that had the identical bug).

function getClientId() {
  return localStorage.getItem("client_id");
}

async function employeesLoader() {
  const clientId = getClientId();
  if (!clientId) return [];

  const res = await fetch(`/api/employees?client_id=${clientId}`);
  const json = await res.json();
  return Array.isArray(json) ? json : json.data || [];
}

async function customersLoader() {
  const clientId = getClientId();
  if (!clientId) return [];

  const res = await fetch(`/api/customers?client_id=${clientId}`);
  const json = await res.json();
  return Array.isArray(json) ? json : json.data || [];
}

async function vendorsLoader() {
  const clientId = getClientId();
  if (!clientId) return [];

  const res = await fetch(`/api/vendors?client_id=${clientId}`);
  const json = await res.json();
  return Array.isArray(json) ? json : json.data || [];
}

async function vehiclesLoader() {
  const clientId = getClientId();
  if (!clientId) return [];

  const res = await fetch(`/api/vehicles?client_id=${clientId}`);
  const json = await res.json();
  return Array.isArray(json) ? json : json.data || [];
}

async function bunksLoader() {
  const clientId = getClientId();
  if (!clientId) return [];

  const res = await fetch(`/api/bunks?client_id=${clientId}`);
  const json = await res.json();
  return Array.isArray(json) ? json : json.data || [];
}

// NOTE: bookingLoader intentionally removed.
// BookingForm.jsx fetches its own data internally (with client_id),
// and never reads loader data, so this loader was dead code that
// failed on every load with "Client ID required".

async function tripsheetLoader() {
  const clientId = getClientId();
  if (!clientId) return [];

  const res = await fetch(`/api/tripsheets?client_id=${clientId}`);
  const json = await res.json();
  return Array.isArray(json) ? json : json.data || [];
}

async function invoicesLoader() {
  const clientId = getClientId();
  if (!clientId) return [];

  const res = await fetch(`/api/invoices?client_id=${clientId}`);
  const json = await res.json();
  return Array.isArray(json) ? json : json.data || [];
}

async function purchasesLoader() {
  const clientId = getClientId();
  if (!clientId) return [];

  const res = await fetch(`/api/purchases?client_id=${clientId}`);
  const json = await res.json();
  return Array.isArray(json) ? json : json.data || [];
}

async function indentLoader() {
  const clientId = getClientId();
  if (!clientId) return [];

  const res = await fetch(`/api/indents?client_id=${clientId}`);
  const json = await res.json();
  return Array.isArray(json) ? json : json.data || [];
}

// ---------------- Loader-aware Wrappers ----------------
function EmployeePage() {
  return <EmployeeForm />;
}

function CustomerPage() {
  const data = useLoaderData();
  return <CustomerForm initialData={data} />;
}

function VendorPage() {
  const data = useLoaderData();
  return <VendorForm initialData={data} />;
}

function VehiclePage() {
  const data = useLoaderData();
  return <VehicleForm initialData={data} />;
}

function VehicleTypePage() {
  const { clientId } = useAppContext();
  return <CategoryForm clientId={clientId} categoryType="VehicleType" />;
}

function VehicleGroupPage() {
  const { clientId } = useAppContext();
  return <CategoryForm clientId={clientId} categoryType="VehicleGroup" />;
}

function BookingPage() {
  return <BookingForm />;
}

function TripsheetPage() {
  const data = useLoaderData();
  return <TripsheetForm initialData={data} />;
}

function BunkPage() {
  const data = useLoaderData();
  return <BunkForm initialData={data} />;
}

function InvoicePage() {
  const data = useLoaderData();
  return <InvoiceForm initialData={data} />;
}

function PurchasePage() {
  const data = useLoaderData();
  return <PurchaseForm initialData={data} />;
}

function IndentPage() {
  const data = useLoaderData();
  return <IndentForm initialData={data} />;
}

// ---------------- Error handling ----------------
function ErrorPage({ error }) {
  return (
    <div style={{ padding: 20 }}>
      <h2>Oops! Something went wrong.</h2>
      <p>{error?.statusText || error?.message || "Unknown error"}</p>
      <p>Please try refreshing or contact support.</p>
    </div>
  );
}

// ---------------- Main App Component ----------------
function App() {
  // ============================================================
  // GLOBAL BROWSER AUTOFILL PREVENTION
  // Prevent Chrome from showing saved names / addresses / phones
  // ============================================================
  useEffect(() => {
    const preventBrowserAutofill = () => {
      // Disable autocomplete on all forms
      document.querySelectorAll("form").forEach((form) => {
        form.setAttribute("autocomplete", "off");
      });

      // Disable Chrome autofill on normal input fields
      document
        .querySelectorAll(
          'input:not([type="password"]):not([type="hidden"]):not([type="submit"])',
        )
        .forEach((input) => {
          input.setAttribute("autocomplete", "new-password");

          // Prevent Chrome from treating common business fields
          // as saved address/contact fields
          input.setAttribute("data-lpignore", "true");
          input.setAttribute("data-form-type", "other");
        });

      // Disable autocomplete on dropdowns
      document.querySelectorAll("select").forEach((select) => {
        select.setAttribute("autocomplete", "off");
      });

      // Disable autocomplete on textareas
      document.querySelectorAll("textarea").forEach((textarea) => {
        textarea.setAttribute("autocomplete", "off");
      });
    };

    // Initial application
    preventBrowserAutofill();

    // React pages/forms are dynamically rendered
    const observer = new MutationObserver(() => {
      preventBrowserAutofill();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const [sidebarMenus, setSidebarMenus] = useState(
    window.history.state?.sidebarMenus || {
      masters: false,
      tariff: false,
      invoice: false,
      purchase: false,
      fuel: false,
      accountant: false,
    },
  );

  useEffect(() => {
    const onPopState = (event) => {
      if (event.state?.sidebarMenus) {
        setSidebarMenus(event.state.sidebarMenus);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* Public Routes */}
        <Route element={<SimpleLayout />}>
          <Route
            path="/"
            element={
              <PublicRoute>
                <HomePage />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />}
          
          />

          <Route
            path="/subscription-expired"
            element={<SubscriptionExpired />}
          />
          <Route
            path="/activation-payment/:invoiceId"
            element={<ActivationPayment />}
          />

          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path="/client-register"
            element={
              <PublicRoute>
                <ClientRegisterPage />
              </PublicRoute>
            }
          />

          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="*" element={<div>Page Not Found</div>} />
        </Route>

        {/* ✅ Protected Routes */}
        <Route path="/*" element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            <Route index element={<HomePage />} />

            <Route
              path="subscription-payment"
              element={<SubscriptionPayment />}
            />

            <Route
              path="driver-app-payment"
              element={<DriverAppPaymentSubmit />}
            />

            {/* Master Data */}
            <Route path="employees" element={<EmployeePage />} />

            <Route
              path="customers"
              element={<CustomerPage />}
              loader={customersLoader}
            />
            <Route
              path="vendors"
              element={<VendorPage />}
              loader={vendorsLoader}
            />
            <Route
              path="vehicles"
              element={<VehiclePage />}
              loader={vehiclesLoader}
            />
            <Route path="vehicle-type" element={<VehicleTypePage />} />
            <Route path="vehicle-group" element={<VehicleGroupPage />} />
            <Route path="hotels" element={<HotelPage />} />

            {/* Bookings / Tripsheets */}
            <Route path="bookings" element={<BookingPage />} />

            <Route
              path="tripsheets"
              element={<TripsheetPage />}
              loader={tripsheetLoader}
              errorElement={<ErrorPage />}
            />

            <Route
              path="tripsheets/tripsheet/add"
              element={<TripsheetFormAdd />}
            />

            {/* Invoices */}
            <Route
              path="invoices"
              element={<InvoicePage />}
              loader={invoicesLoader}
              errorElement={<ErrorPage />}
            >
              <Route path="add" element={<InvoiceFormLocal />} />
              <Route path=":invoiceId/edit" element={<InvoiceFormLocal />} />
            </Route>

            {/* Purchases */}
            <Route
              path="purchases"
              element={<PurchasePage />}
              loader={purchasesLoader}
              errorElement={<ErrorPage />}
            >
              <Route path="add" element={<PurchaseFormAdd />} />
              <Route path=":purchaseId/edit" element={<PurchaseFormAdd />} />
            </Route>

            <Route path="reports" element={<ReportsDashboard />} />

            <Route path="reports/customers" element={<CustomersReport />} />
            <Route path="reports/vehicles" element={<VehicleReport />} />
            <Route path="reports/vendors" element={<VendorReport />} />
            <Route path="reports/bookings" element={<BookingReport />} />
            <Route path="reports/tariff-print" element={<TariffReport />} />
            <Route path="reports/fuel-indents" element={<FuelIndentReport />} />
            <Route
              path="reports/trip-statement"
              element={<TripStatementReport />}
            />
            <Route
              path="reports/comparison-statement"
              element={<ComparisonReport />}
            />
            <Route path="reports/sale-register" element={<SaleRegister />} />
            <Route
              path="reports/purchase-register"
              element={<PurchaseReport />}
            />
            <Route path="reports/gst-report" element={<GSTReport />} />
            <Route
              path="reports/mis-reports"
              element={<MISCustomerVehicle />}
            />

            {/* Accountant / Fuel */}
            <Route path="accounts-chart" element={<AccountsChart />} />
            <Route path="vouchers" element={<VoucherList />} />
            <Route
              path="accountant/accounts-reports"
              element={<AccountsReportsDashboard />}
            />
            <Route
              path="accountant/ledger-statement"
              element={<LedgerStatement />}
            />
            <Route
              path="accountant/outstanding-statement"
              element={<OutstandingStatement />}
            />

            <Route path="bunks" element={<BunkPage />} loader={bunksLoader} />

            {/* ✅ Fuel Indent Routes */}
            <Route
              path="indent"
              element={<IndentPage />}
              loader={indentLoader}
              errorElement={<ErrorPage />}
            />
            <Route path="indent/add" element={<IndentFormAdd />} />
            <Route path="indent/:id/edit" element={<IndentFormAdd />} />

<Route
  path="encryption-test"
  element={<EncryptionTest />}
/>
            {/* User / Settings */}
            <Route path="profile" element={<UserProfilePage />} />
            <Route path="settings" element={<SettingsRoute />} />
            <Route path="simple-client" element={<SimpleClientData />} />

            {/* ⚠️ KEEP THIS AT THE VERY END */}
            <Route path="*" element={<div>Page Not Found</div>} />
          </Route>
        </Route>
      </>,
    ),
  );

  return (
    <AuthProvider>
      <AppProvider>
        <SnackbarAlertProvider>
          <RouterProvider router={router} />
        </SnackbarAlertProvider>
        <ToastContainer position="top-right" autoClose={3000} />
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
